import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '../Button';
import FontAwesomeIcon from '../Icon';
import Overlay from '../Overlay';
import MaintenanceGuideRepeater from './MaintenanceGuideRepeater';
import Empty from '../Empty';
import { setFndFormData, clearFndFormData } from 'src/store/actions/fndForm';
import ContentBlock from '../ContentBlock';
import { IntroContent } from 'src/common/constants';
import MaintenanceGuideForm from 'src/components/MaintenanceGuideForm/MaintenanceGuideForm';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import Search from 'src/components/Search/Search';
import '../NoteFormNewLocalState/Notes.scss';
import './MaintenanceGuides.scss';

interface MaintenanceGuidesProps {
  from: any;
  maintenanceguides: any[];
  onlyView?: boolean;
  onSaveMaintenanceGuide: any;
  onDeleteNote: (guide: any) => Promise<void>;
  breadcrumb: any;
  onSearch: any;
  introContent?: boolean;
  searchTerm: string;
}

const MaintenanceGuides = ({
  from,
  maintenanceguides,
  onlyView,
  onSaveMaintenanceGuide,
  onDeleteNote,
  breadcrumb,
  onSearch,
  introContent,
  searchTerm,
}: MaintenanceGuidesProps) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [guideToDelete, setGuideToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const dispatch: any = useDispatch();

  const onEditMaintenanceGuide = async (maintenanceguide: any) => {
    await dispatch(clearFndFormData() as any);

    if (maintenanceguide.appointment) {
      let date: Date = new Date(maintenanceguide.appointment.date);
      let time: Date =
        maintenanceguide.appointment.time && maintenanceguide.appointment.time
          ? new Date(maintenanceguide.appointment.time)
          : null;

      let appointment: any = {
        date,
        time,
      };

      maintenanceguide = { ...maintenanceguide, appointment, toggle: true };
    }

    dispatch(setFndFormData(maintenanceguide) as any);
    setShowOverlay(true);
  };

  const handleDeleteConfirm = async () => {
    if (!guideToDelete) return;

    setIsDeleting(true);
    await onDeleteNote(guideToDelete);
    setIsDeleting(false);
    setGuideToDelete(null);
  };

  return (
    <div className="notes">
      {introContent && (
        <ContentBlock content={IntroContent.maintenanceReminders} />
      )}
      <div className="maintenance-guides-header">
        <div className="maintenance-guides-search">
          <Search
            size="small"
            placeholder="Search Maintenance Reminders..."
            onChange={onSearch}
            theme={undefined}
          />
        </div>
        {onlyView === true ? null : (
          <Button
            color="teal"
            label="Add Maintenance Reminder"
            onClick={() => {
              dispatch(clearFndFormData() as any);
              setShowOverlay(true);
            }}
            icon={<FontAwesomeIcon glyph="edit" weight="light" size="small" />}
            size="small"
          />
        )}
      </div>
      {maintenanceguides.length > 0 ? (
        <div className="maintenance-guides-scroll">
          <MaintenanceGuideRepeater
            from={from}
            onlyView={onlyView}
            maintenanceguides={maintenanceguides}
            onEdit={(maintenanceguide: any) =>
              onEditMaintenanceGuide(maintenanceguide)
            }
            onDelete={(maintenanceguide: any) =>
              setGuideToDelete(maintenanceguide)
            }
          />
        </div>
      ) : (
        <Empty
          headline="No Maintenance Reminders Added yet"
          strapline="You can use the Maintenance Reminders section to post any status updates or general comments to keep everyone on the same page."
          searchIsActive={searchTerm}
          cta={
            onlyView === true ? null : (
              <Button
                color="teal"
                label="Add Maintenance Reminders"
                onClick={() => {
                  dispatch(clearFndFormData() as any);
                  setShowOverlay(true);
                }}
                icon={
                  <FontAwesomeIcon glyph="edit" weight="light" size="small" />
                }
                size="small"
              />
            )
          }
          image={
            <img
              alt="No Maintenance Reminders yet"
              src="/assets/empty/notes.svg"
            />
          }
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <MaintenanceGuideForm
            breadcrumb={breadcrumb}
            onSaveMaintenanceGuide={onSaveMaintenanceGuide}
            onClose={() => {
              setShowOverlay(false);
              dispatch(clearFndFormData() as any);
            }}
          />
        }
      />
      {guideToDelete && (
        <ConfirmationModal
          title="Delete Maintenance Reminder"
          message={
            <>
              Are you sure you want to delete{' '}
              <strong>{guideToDelete.title}</strong>?
            </>
          }
          warningMessage="This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          isSubmitting={isDeleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setGuideToDelete(null)}
        />
      )}
    </div>
  );
};

export default MaintenanceGuides;
